import React, { useId, useState } from 'react';
import {
  IconAlertCircle,
  IconCalendar,
  IconCheckbox,
  IconChevronDown,
  IconCircleCheck,
  IconCircleDashed,
  IconCircleDot,
  IconClock,
  IconHash,
  IconHelpCircle,
  IconMail,
  IconPhoto,
  IconPlayerSkipForward,
  IconSparkles,
  IconTextCaption,
} from '@tabler/icons-react';
import type {
  ConversationProgressDocument,
  ConversationProgressFieldType,
  ConversationProgressStep,
  ConversationProgressStepState,
} from '../../types';

/** Props for the read-only admin progress summary. */
export interface ConversationProgressCardProps {
  /** Durable progress document, or null for a legacy response. */
  progress?: ConversationProgressDocument | null;
  /** A progress-only read or subscription failed. */
  unavailable?: boolean;
  /** Whether a legacy response has enough data to merit an unavailable card. */
  showLegacyUnavailable?: boolean;
  /** Opens recorded-call evidence; omitted when the response has no transcript tab. */
  onEvidenceTurnSelect?: (turnId: string) => void;
}

interface StatusPresentation {
  /** Screen-reader label for the state. */
  label: string;
  /** Semantic theme token for the current state icon. */
  color: string;
  /** Tabler glyph for the state. */
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
}

const STATUS_ORDER: ConversationProgressStepState[] = [
  'pending',
  'active',
  'completed',
  'skipped',
  'failed',
];

const STATUS_PRESENTATION: Record<ConversationProgressStepState, StatusPresentation> = {
  pending: { label: 'Pending', color: 'text-muted-foreground', icon: IconCircleDashed },
  active: { label: 'Active', color: 'text-warning-soft', icon: IconClock },
  completed: { label: 'Completed', color: 'text-success-soft', icon: IconCircleCheck },
  skipped: { label: 'Skipped', color: 'text-muted-foreground', icon: IconPlayerSkipForward },
  failed: { label: 'Failed', color: 'text-danger-soft', icon: IconAlertCircle },
};

/** Return the small type glyph for an authored field. */
function fieldTypeIcon(fieldType: ConversationProgressFieldType): React.ReactNode {
  const iconClass = 'h-4 w-4';
  switch (fieldType) {
    case 'free_form_explore':
      return <IconSparkles aria-hidden="true" className={iconClass} />;
    case 'information':
      return <IconPhoto aria-hidden="true" className={iconClass} />;
    case 'calendar_booking':
      return <IconCalendar aria-hidden="true" className={iconClass} />;
    case 'short_text':
      return <IconTextCaption aria-hidden="true" className={iconClass} />;
    case 'number':
      return <IconHash aria-hidden="true" className={iconClass} />;
    case 'email':
      return <IconMail aria-hidden="true" className={iconClass} />;
    case 'choice_single':
      return <IconCircleDot aria-hidden="true" className={iconClass} />;
    case 'choice_multi':
      return <IconCheckbox aria-hidden="true" className={iconClass} />;
    case 'unsupported':
      return <IconHelpCircle aria-hidden="true" className={iconClass} />;
    default:
      return <IconHelpCircle aria-hidden="true" className={iconClass} />;
  }
}

/** Return an admin-safe display label when compilation could not retain one. */
function displayLabel(step: ConversationProgressStep): string {
  const configured = step.displayLabel.trim();
  if (configured) return configured;
  if (step.fieldType === 'calendar_booking') return 'Calendar booking';
  if (step.fieldType === 'information') return 'Information block';
  return 'Conversation step';
}

/** Build an accessible summary of state and any safe reason. */
function stepAriaLabel(step: ConversationProgressStep): string {
  const label = displayLabel(step);
  const reason = step.state === 'skipped'
    ? step.skipReason ?? step.reasonCode
    : step.state === 'failed'
      ? step.failureReason ?? step.reasonCode
      : undefined;
  const safeReason = reason
    ? `${reason.charAt(0).toUpperCase()}${reason.slice(1).replaceAll('_', ' ')}`
    : undefined;
  if (step.state === 'skipped' && safeReason) {
    return `${label}. Skipped: ${safeReason}`;
  }
  if (step.state === 'failed' && safeReason) {
    return `${label}. Failed: ${safeReason}`;
  }
  return `${label}. ${STATUS_PRESENTATION[step.state].label}`;
}

/** Prefer the reducer identity while keeping staggered legacy rows readable. */
function progressStepKey(step: ConversationProgressStep): string {
  const reducerFieldId = step.fieldId?.trim();
  if (reducerFieldId) return reducerFieldId;
  const legacyStepId = step.stepId?.trim();
  if (legacyStepId) return legacyStepId;
  return `${step.fieldName}:${step.schemaPosition}`;
}

/** Cross-fading status glyph with every possible state kept mounted. */
function StepStatus({ step }: { step: ConversationProgressStep }) {
  const current = STATUS_PRESENTATION[step.state];
  const stepKey = progressStepKey(step);
  return (
    <span
      role="img"
      aria-label={current.label}
      data-testid={`step-status-${stepKey}`}
      className={`grid h-4 w-4 shrink-0 ${current.color}`}
    >
      {STATUS_ORDER.map((state) => {
        const presentation = STATUS_PRESENTATION[state];
        const StatusIcon = presentation.icon;
        const selected = state === step.state;
        return (
          <span
            key={state}
            aria-hidden="true"
            className={`col-start-1 row-start-1 transition-[opacity,transform] motion-reduce:transition-none ${
              selected
                ? 'translate-x-0 opacity-100 delay-[160ms] duration-[600ms]'
                : '-translate-x-4 opacity-0 pointer-events-none delay-0 duration-200'
            }`}
          >
            <StatusIcon className="h-4 w-4" />
          </span>
        );
      })}
    </span>
  );
}

/** Count display with every valid value pre-rendered for a fluid state hand-off. */
function CoveredCount({ covered, total }: { covered: number; total: number }) {
  return (
    <span className="grid min-w-0 text-sm text-muted-foreground" role="status" aria-live="polite" aria-atomic="true">
      {Array.from({ length: total + 1 }, (_, value) => {
        const selected = value === covered;
        return (
          <span
            key={value}
            aria-hidden={!selected}
            data-testid={selected ? 'progress-count-current' : undefined}
            data-current={selected ? 'true' : undefined}
            className={`col-start-1 row-start-1 whitespace-nowrap transition-[opacity,transform] motion-reduce:transition-none ${
              selected
                ? 'translate-x-0 opacity-100 delay-[160ms] duration-[600ms]'
                : '-translate-x-4 opacity-0 pointer-events-none delay-0 duration-200'
            }`}
          >
            <span data-testid="progress-count-state">{value} of {total} covered</span>
          </span>
        );
      })}
    </span>
  );
}

/** One schema-ordered progress row. */
function ProgressStepRow({
  step,
  onEvidenceTurnSelect,
}: {
  step: ConversationProgressStep;
  onEvidenceTurnSelect?: (turnId: string) => void;
}) {
  const label = displayLabel(step);
  const content = (
    <>
      <span aria-hidden="true" className="shrink-0 text-muted-foreground">
        {fieldTypeIcon(step.fieldType)}
      </span>
      <span className="min-w-0 flex-1 truncate text-left text-sm text-foreground" title={label}>
        {label}
      </span>
      <StepStatus step={step} />
    </>
  );

  return (
    <li
      role="listitem"
      data-testid="conversation-progress-step"
      aria-label={!onEvidenceTurnSelect || step.evidenceTurnIds.length === 0 ? stepAriaLabel(step) : undefined}
      className="min-w-0"
    >
      {onEvidenceTurnSelect && step.evidenceTurnIds.length > 0 ? (
        <button
          type="button"
          aria-label={`Open ${label} in transcript`}
          onClick={() => onEvidenceTurnSelect(step.evidenceTurnIds[0])}
          className="flex min-h-11 w-full cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt motion-reduce:transition-none"
        >
          {content}
          <span className="sr-only">{stepAriaLabel(step)}</span>
        </button>
      ) : (
        <div className="flex min-h-11 items-center gap-3 px-2 py-2">
          {content}
        </div>
      )}
    </li>
  );
}

/**
 * Compact, read-only overview of durable conversation coverage. Detail expands
 * inline and scrolls with the page on touch devices.
 */
export function ConversationProgressCard({
  progress,
  unavailable = false,
  showLegacyUnavailable = false,
  onEvidenceTurnSelect,
}: ConversationProgressCardProps) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const showUnavailable = unavailable || !progress || progress.mode === 'legacy';

  if (progress?.mode === 'shadow') return null;
  if (showUnavailable && !showLegacyUnavailable && !unavailable) return null;

  const orderedSteps = progress
    ? [...progress.steps].sort((left, right) => left.schemaPosition - right.schemaPosition)
    : [];
  const covered = orderedSteps.filter((step) => step.state === 'completed').length;
  const total = orderedSteps.length;
  const coverage = total > 0 ? covered / total : 0;

  return (
    <section className="rounded-2xl border border-border/50 bg-background/50 p-8" aria-labelledby={`${detailsId}-heading`}>
      <div className="flex items-center justify-between gap-4">
        <h3 id={`${detailsId}-heading`} className="text-sm font-medium text-foreground">Progress</h3>
        {!showUnavailable && (
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={detailsId}
            aria-label={expanded ? 'Hide progress details' : 'Show progress details'}
            onClick={() => setExpanded((current) => !current)}
            className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt motion-reduce:transition-none"
          >
            <IconChevronDown
              aria-hidden="true"
              className={`h-4 w-4 transition-transform duration-500 ease-brand motion-reduce:transition-none ${expanded ? 'rotate-180' : 'rotate-0'}`}
            />
          </button>
        )}
      </div>

      {showUnavailable ? (
        <p className="mt-4 text-sm text-muted-foreground">Unavailable</p>
      ) : (
        <>
          <div className="mt-4 space-y-3">
            <CoveredCount covered={covered} total={total} />
            <div
              role="progressbar"
              aria-label="Conversation coverage"
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuenow={covered}
              className="h-2 w-full overflow-hidden rounded-full bg-surface"
            >
              <span
                aria-hidden="true"
                className="block h-full w-full origin-left bg-success transition-transform duration-500 ease-brand motion-reduce:transition-none"
                style={{ transform: `scaleX(${coverage})` }}
              />
            </div>
          </div>

          <div
            id={detailsId}
            data-testid="conversation-progress-details"
            aria-hidden={!expanded}
            inert={!expanded}
            className={`grid transition-[grid-template-rows,opacity] duration-500 ease-brand motion-reduce:transition-none ${
              expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
            }`}
          >
            <div className="min-h-0 overflow-hidden">
              <ol
                aria-label="Conversation steps"
                className="mt-4 space-y-1 border-t border-border-soft pt-4 scrollbar-dark pointer-fine:max-h-[480px] pointer-fine:overflow-y-auto pointer-fine:overscroll-contain"
              >
                {orderedSteps.map((step) => (
                  <ProgressStepRow
                    key={progressStepKey(step)}
                    step={step}
                    onEvidenceTurnSelect={onEvidenceTurnSelect}
                  />
                ))}
              </ol>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export default ConversationProgressCard;
